/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import Button from "components/Button/Button";
import InputField from "components/InputField/InputField";
import "pages/Logs/MarketplaceLogs/ShopInvoice.scss";
import "react-tabs/style/react-tabs.css";
import {
  IFailedInvoicesQuery,
  MarkInvoiceAsPaidInput,
  markInvoiceAsPaidMutation,
} from "../graphql";
import usePermission from "hooks/usePermission";
import { InvoiceStatus, PermissionEnum } from "TypesAndInterfaces";
import Modal from "components/Modal/Modal";
import Checkbox from "components/Checkbox/Checkbox";
import { useMutation } from "@apollo/client";
import { displaySuccess, displayError } from "utils/displayNotification";
import TooltipButton from "components/TooltipButton/TooltipButton";

interface IProps {
  data: IFailedInvoicesQuery[];
  disableButtons: boolean;
  handleRetryInvoices: (ids: string) => void;
  handleRestartInvoices: (ids: string) => void;
  refetch: () => void;
  adminView: boolean
}

interface IInvoiceStatus {
  payout: InvoiceStatus;
  commission: InvoiceStatus;
  subscription: InvoiceStatus;
}

export const InvoicesTable = ({
  data,
  handleRetryInvoices,
  handleRestartInvoices,
  disableButtons,
  refetch,
  adminView
}: IProps) => {
  //#region hooks
  const [filteredData, setFilteredData] = useState<IFailedInvoicesQuery[]>([]);
  const invoiceRef = useRef<HTMLInputElement>(null);
  const shopRef = useRef<HTMLInputElement>(null);
  const { isAuthorized } = usePermission(PermissionEnum.ADMIN);
  const [isResetModalOpened, setIsResetModalOpened] = useState(false);
  const [invoiceToReset, setInvoiceToReset] = useState("");
  const [invoiceToResetLength, setInvoiceToResetLength] = useState<number>(0);
  const [invoiceToRetry, setInvoiceToRetry] = useState("");
  const [invoiceToRetryLength, setInvoiceToRetryLength] = useState<number>(0);
  const [isRetryModalOpened, setIsRetryModalOpened] = useState(false);
  const [isManualModalOpened, setIsManualModalOpened] = useState(false);
  const [manualUpdateInvoice, setManualUpdateInvoice] =
    useState<IFailedInvoicesQuery>();
  const [manualUpdateStatus, setManualUpdateStatus] = useState<IInvoiceStatus>({
    payout: InvoiceStatus.None,
    commission: InvoiceStatus.None,
    subscription: InvoiceStatus.None,
  });
  const [isMultipleResetSelected, setIsMultipleResetSelected] = useState<boolean>(false);
  const [isMultipleRetrySelected, setIsMultipleRetrySelected] = useState<boolean>(false);
  const [clearSelectedRows, setClearSelectedRows] = useState<boolean>(false);
  //#endregion

  const [markInvoiceAsPaid] = useMutation<
    { markInvoiceAsPaid: boolean },
    { data: MarkInvoiceAsPaidInput }
  >(markInvoiceAsPaidMutation, {
    onCompleted: (data) => {
      setIsManualModalOpened(false);
      data && displaySuccess("Sync succeeded");
      refetch();
    },
    onError: (e) => {
      setIsManualModalOpened(false);
      displayError(e.message);
    },
  });

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const customSort = (rows: any, selector: Function, direction: string) => {
    return rows.sort((rowA: any, rowB: any) => {
      let aField = selector(rowA);
      let bField = selector(rowB);

      let comparison = 0;

      if (aField && aField.props) {
        aField = JSON.stringify(aField.props);
      }

      if (bField && bField.props) {
        bField = JSON.stringify(bField.props);
      }

      if (aField > bField) {
        comparison = 1;
      } else if (aField < bField) {
        comparison = -1;
      }

      return direction === "desc" ? comparison * -1 : comparison;
    });
  };

  const setFilterState = (property: string, value: any) => {
    if (property === "invoiceId") {
      const filtered = data.filter((item) =>
        item.invoiceId.toString().includes(value)
      );
      if (filtered) {
        setFilteredData(filtered);
      }
    } else {
      const filtered = data.filter((item) =>
        item.shopId.toString().includes(value)
      );
      if (filtered) {
        setFilteredData(filtered);
      }
    }
  };

  const resetFields = () => {
    invoiceRef.current!.value = "";
    shopRef.current!.value = "";
    setFilteredData(data);
  };

  const readableName = (text: string) => {
    if (text === "retryCount") {
      return "Attempts Left"
    } else {
      return text
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());
    }
  };

  const sortableColumns = ["invoiceId", "shopId", "balanceAccountID"];
  const grewColumnsMapping: { [key: string]: number } = {
    "date": 2,
  }
  const customCellMapping: { [key: string]: (row: IFailedInvoicesQuery) => React.JSX.Element } = {
    "balanceAccountID": (row: IFailedInvoicesQuery) => <a href={`${process.env.REACT_APP_BALANCE_PLATFORM_BASE_PATH}${row.balanceAccountID}`} target="_blank" rel="noreferrer">{row.balanceAccountID}</a>
  }

  const customCellCommisionMapping: { [key: string]: (row: IFailedInvoicesQuery) => React.JSX.Element } = {
    "commission": (row: IFailedInvoicesQuery) => <div>{+row.commission.toFixed(2)}</div>
  }

  const customCellAttemptsLeft: { [key: string]: (row: IFailedInvoicesQuery) => React.JSX.Element } = {
    "retryCount": (row: IFailedInvoicesQuery) => <div> {Math.abs(row.retryCount)}</div>
  }

  const columns: TableColumn<IFailedInvoicesQuery>[] = [
    ...Object.keys(data.length > 0 && data[0])
      .filter(
        (key) => key !== "canRetry"
      )
      .map((key) => ({
        name: readableName(key),
        sortable: sortableColumns.includes(key),
        grow: grewColumnsMapping[key] || 1,
        cell: customCellMapping[key] || customCellCommisionMapping[key] || customCellAttemptsLeft[key] || ((row: IFailedInvoicesQuery) => row[key]),
        selector: (row: IFailedInvoicesQuery) => row[key],
      })),
    {
      name: "Action",
      grow: 4,
      minWidth: "195px",
      cell: (row) => (
        <>
          <Button
            className={row.canRetry ? "button--bordered button--green invoice-table-controls" : "button--bordered button--red invoice-table-controls"}
            type="button"
            content={row.canRetry ? "Retry" : "Reset"}
            icon="sync"
            onClick={() => handleRowClicked(row)}
            disabled={!isAuthorized || disableButtons}
          ></Button>
          &nbsp;
          <Button
            className="button--bordered button--green invoice-table-controls"
            type="button"
            content="Manually Paid"
            icon="pencil"
            onClick={() => handleManualUpdateClicked(row)}
            disabled={!isAuthorized || disableButtons}
          ></Button>
        </>
      ),
    },
  ];

  const handleRowClicked = (row: IFailedInvoicesQuery) => {
    if (row.canRetry) {
      setIsRetryModalOpened(true);
      setInvoiceToRetry(row.invoiceId.toString());
    } else {
      setIsResetModalOpened(true);
      setInvoiceToReset(row.invoiceId.toString());
    }
  };

  const handleManualUpdateClicked = (row: IFailedInvoicesQuery) => {
    setManualUpdateInvoice(row);
    setManualUpdateStatus({
      payout: row.payoutStatus,
      commission: row.commissionStatus,
      subscription: row.subscriptionStatus,
    });
    setIsManualModalOpened(true);
  };

  const handleManualUpdateInvoices = () => {
    const id = localStorage.getItem("marketplaceId");
    const marketplaceId = Number(id);
    if (marketplaceId && manualUpdateInvoice) {
      const { commission, payout, subscription } = manualUpdateStatus;
      const { invoiceId } = manualUpdateInvoice;
      markInvoiceAsPaid({
        variables: {
          data: {
            invoiceId,
            payout: payout === InvoiceStatus.Succeeded,
            commission: commission === InvoiceStatus.Succeeded,
            subscription: subscription === InvoiceStatus.Succeeded,
            marketplaceId,
          },
        },
      });
    }
  };

  const handleSearch = (searchParam: string, searchValue: string) => {
    setFilterState(searchParam, Number(searchValue));
  };

  useEffect(() => {
    invoiceToReset.length === 0 ? setInvoiceToResetLength(0) : setInvoiceToResetLength(invoiceToReset.split(",").length)
  }, [invoiceToReset])

  useEffect(() => {
    invoiceToRetry.length === 0 ? setInvoiceToRetryLength(0) : setInvoiceToRetryLength(invoiceToRetry.split(",").length)
  }, [invoiceToRetry])


  const tooltipClass = adminView 
  ? "tooltip-button--bordered button--green admin-view-class" 
  : "tooltip-button--bordered button--green";

  const buttonClass = adminView 
  ? "button--bordered button--green admin-view-class" 
  : "button--bordered button--green";

  const conditionalRowStyles = [
    {
      when: (row: any) => isMultipleRetrySelected && !row.canRetry,
      style: {
        backgroundColor: "#DDDDDD",
        color: "#808080", 
        cursor: "not-allowed", 
      },
    },
  ];

  return (
    <main className="main-container-wrapper">
      <section className="main-container">
        <header className="main-container__header">
          <h2 className="main-container__title">Failed Invoices</h2>
          <div className="header-buttons">
            {(isMultipleResetSelected || isMultipleRetrySelected) ? (
              <div className="left-buttons">
                {isMultipleResetSelected && (
                  <Button
                    className={buttonClass}
                    type="button"
                    content={"Reset " + invoiceToResetLength + " invoices"}
                    icon="sync"
                    onClick={() => setIsResetModalOpened(true)}
                    disabled={invoiceToReset.length === 0}

                  ></Button>
                )}
                {isMultipleRetrySelected && (
                  <Button
                    className={buttonClass}
                    type="button"
                    content={"Retry " + invoiceToRetryLength + " invoices"}
                    icon="sync"
                    onClick={() => setIsRetryModalOpened(true)}
                    disabled={invoiceToRetryLength === 0}
                  ></Button>
                )}
                <Button
                  className="button--bordered button--red"
                  type="button"
                  content="Cancel"
                  onClick={() => {
                    setIsMultipleResetSelected(false);
                    setIsMultipleRetrySelected(false);
                    setClearSelectedRows(!clearSelectedRows);
                    setInvoiceToRetry("");
                    setInvoiceToRetryLength(0);
                    setInvoiceToReset("");
                    setInvoiceToResetLength(0);
                  }}
                ></Button>
              </div>
            ) : (
              <>
                <TooltipButton
                  className={tooltipClass}
                  type="button"
                  content={"Reset multiple invoices"}
                  icon="sync"
                  onClick={() => setIsMultipleResetSelected(!isMultipleResetSelected)}
                  disabled={!isAuthorized || disableButtons}
                  tooltip="Resets the retry count and reprocesses invoices. Available only if the retry count is 0"
                ></TooltipButton>
                <TooltipButton
                  className={tooltipClass}
                  type="button"
                  content={"Retry multiple invoices"}
                  icon="sync"
                  onClick={() => setIsMultipleRetrySelected(!isMultipleRetrySelected)}
                  disabled={!isAuthorized || disableButtons}
                  tooltip="Attempts to process failed invoices again. Available only if the retry count is greater than 0"
                ></TooltipButton>
              </>
            )} </div>
        </header>
        <div className="main-content marketplace-logs-container">
          <aside className="main-content__sidebar">
            <header className="main-content__sidebar__header">
              <h4 className="main-content__sidebar__title">Filters</h4>
              <Button
                className="button--small button--rounded button--no-wrap"
                onClick={resetFields}
                type="button"
                content="RESET ALL"
              ></Button>
            </header>
            <div className="filters">
              <div className="logs-id-filters">
                <span>
                  <InputField
                    innerRef={invoiceRef}
                    numeric
                    label="Invoice Id"
                    onChange={(e) =>
                      handleSearch("invoiceId", e.currentTarget.value)
                    }
                  />
                </span>
                <span>
                  <InputField
                    innerRef={shopRef}
                    numeric
                    label="Shop Id"
                    onChange={(e) => {
                      handleSearch("shopId", e.currentTarget.value);
                    }}
                  />
                </span>
              </div>
            </div>
          </aside>
          <div className="main-content__result marketplace-logs-table">
            <DataTable
              sortFunction={customSort}
              clearSelectedRows={clearSelectedRows}
              data={filteredData}
              columns={columns}
              selectableRows={isMultipleResetSelected || isMultipleRetrySelected}
              onSelectedRowsChange={(e) => {
                const invoices: string[] = []
                e.selectedRows.forEach((row) => {
                  invoices.push(row.invoiceId.toString())
                });
                isMultipleResetSelected ? setInvoiceToReset(invoices.toString()) : setInvoiceToRetry(invoices.toString());

              }}
              selectableRowDisabled={(row) => isMultipleRetrySelected && !row.canRetry}
              conditionalRowStyles={conditionalRowStyles}
            />
          </div>
        </div>
      </section>
      <Modal
        isOpened={isResetModalOpened}
        setIsOpenedCallback={() => setIsResetModalOpened(false)}
        title="Reset Invoice"
      >
        {isAuthorized ?
          <>
            <div className="modal__content">
              <p className="modal__text">
                Are you sure you want to reset {invoiceToResetLength <= 5 ? "invoice " + invoiceToReset : invoiceToResetLength + " invoices"}?
              </p>
            </div>
            <div className="modal__footer">
              <Button
                className="button--bordered button--green"
                type="button"
                content="Reset"
                onClick={() => {
                  handleRestartInvoices(invoiceToReset);
                  setClearSelectedRows(!clearSelectedRows);
                  setInvoiceToReset("");
                  setInvoiceToResetLength(0);
                  setIsMultipleResetSelected(false);
                  setIsResetModalOpened(false);
                  refetch();
                }}
              ></Button>{" "}
              <Button
                className="button--bordered button--red"
                type="button"
                content="Cancel"
                onClick={() => setIsResetModalOpened(false)}
              ></Button>
            </div>
          </> :
          <p className="modal__text">
            You dont have permissions to do this action.
          </p>}
      </Modal>
      <Modal
        isOpened={isRetryModalOpened}
        setIsOpenedCallback={() => setIsRetryModalOpened(false)}
        title="Retry Invoice"
      >
        <div className="modal__content">
          <p className="modal__text">
            Are you sure you want to retry invoice {invoiceToRetry}? <br />
            By retrying you will decrease the number of automatic retries by
            one.
          </p>
        </div>
        <div className="modal__footer">
          <Button
            className="button--bordered button--green"
            type="button"
            content="Retry"
            onClick={() => {
              handleRetryInvoices(invoiceToRetry);
              setClearSelectedRows(!clearSelectedRows);
              setInvoiceToRetry("");
              setInvoiceToRetryLength(0);
              setIsMultipleRetrySelected(false);
              setIsRetryModalOpened(false);
              refetch();
            }}
          ></Button>{" "}
          <Button
            className="button--bordered button--red"
            type="button"
            content="Cancel"
            onClick={() => setIsRetryModalOpened(false)}
          ></Button>
        </div>
      </Modal>
      <Modal
        isOpened={isManualModalOpened}
        setIsOpenedCallback={() => setIsManualModalOpened(false)}
        title="Manual Processing"
      >
        <div className="modal__content">
          <p className="modal__text">
            Which step of the invoice{" "}
            {manualUpdateInvoice?.invoiceId.toString()} have you manually
            processed?
          </p>
        </div>
        <p className="manual-invoice-update__checkboxes">
          <Checkbox
            label="Payout"
            checked={manualUpdateStatus.payout === InvoiceStatus.Succeeded}
            onChange={(e) =>
              setManualUpdateStatus({
                ...manualUpdateStatus,
                payout: e.currentTarget.checked
                  ? InvoiceStatus.Succeeded
                  : InvoiceStatus.None,
              })
            }
            disabled={
              manualUpdateInvoice?.payoutStatus === InvoiceStatus.Succeeded ||
              manualUpdateInvoice?.payoutStatus === InvoiceStatus.Initiated
            }
          ></Checkbox>
          <Checkbox
            label="Commission"
            checked={manualUpdateStatus.commission === InvoiceStatus.Succeeded}
            onChange={(e) =>
              setManualUpdateStatus({
                ...manualUpdateStatus,
                commission: e.currentTarget.checked
                  ? InvoiceStatus.Succeeded
                  : InvoiceStatus.None,
              })
            }
            disabled={
              manualUpdateInvoice?.commissionStatus === InvoiceStatus.Succeeded ||
              manualUpdateInvoice?.commissionStatus === InvoiceStatus.Initiated
            }
          ></Checkbox>
          <Checkbox
            label="Subscription"
            checked={
              manualUpdateStatus.subscription === InvoiceStatus.Succeeded
            }
            onChange={(e) =>
              setManualUpdateStatus({
                ...manualUpdateStatus,
                subscription: e.currentTarget.checked
                  ? InvoiceStatus.Succeeded
                  : InvoiceStatus.None,
              })
            }
            disabled={
              manualUpdateInvoice?.subscriptionStatus === InvoiceStatus.Succeeded ||
              manualUpdateInvoice?.subscriptionStatus === InvoiceStatus.Initiated
            }
          ></Checkbox>
        </p>
        <p>
          <Button
            className="button--bordered button--green"
            type="button"
            content="Update"
            onClick={() => {
              handleManualUpdateInvoices();
            }}
          ></Button>{" "}
          <Button
            className="button--bordered button--red"
            type="button"
            content="Cancel"
            onClick={() => setIsManualModalOpened(false)}
          ></Button>
        </p>
      </Modal>
    </main>
  );
};